import { useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import ListGroup from 'react-bootstrap/ListGroup'
import Spinner from 'react-bootstrap/Spinner'
import { addReceiptItem, deleteReceiptItem, updateReceiptItem, type GroupMember, type Receipt, type ReceiptItem } from '../../../../types'
import { actionApiCall } from '../../api/actionApiCall'
import { AmountField, TextField } from '../../components/form'
import { useCurrencies } from '../../hooks/useCurrencies'
import { exponentOf, formatAmount, formatMoney, parseAmount } from '../../utils/money'
import { ReceiptItemRow } from '../receipts/ReceiptItemRow'

/**
 * Hodnota položek účtenky připadající na jednotlivé osoby, v pořadí členů.
 *
 * Posílá se na server jako váha podílu (režim ITEMS) - server z ní celkovou
 * částku rozdělí poměrně. Položka sdílená více lidmi se dělí rovným dílem.
 */
export function vahyZPolozek(receipt: Receipt, members: GroupMember[]): Array<{ memberId: string; weight: number }> {
    const vahy = new Map<string, number>()
    for (const item of receipt.items) {
        for (const memberId of item.memberIds) {
            vahy.set(memberId, (vahy.get(memberId) ?? 0) + item.totalPrice / item.memberIds.length)
        }
    }

    return members
        .filter((member) => (vahy.get(member.id) ?? 0) > 0)
        .map((member) => ({ memberId: member.id, weight: vahy.get(member.id) ?? 0 }))
}

/** Položky, které ještě nikomu nepatří - bez nich nejde útratu podle položek založit. */
export function nepriradenePolozky(receipt: Receipt) {
    return receipt.items.filter((item) => item.memberIds.length === 0)
}

export type ReceiptItemsSplitProps = {
    receipt: Receipt
    members: GroupMember[]
    /** Celková částka útraty z formuláře v nejmenších jednotkách její měny. */
    amountTotal: number
    /** Měna útraty z formuláře. */
    currencyCode: string
    disabled?: boolean
    onReceiptChange: (receipt: Receipt) => void
}

/**
 * Rozdělení útraty podle položek účtenky při její kontrole.
 *
 * Přiřazení osob se ukládá rovnou k položkám na serveru, takže si položky
 * může postupně rozebrat každý ze skupiny a po potvrzení útraty zůstane
 * u účtenky vidět, kdo co měl. Položku, kterou rozpoznávání vynechalo, jde
 * doplnit ručně.
 */
export function ReceiptItemsSplit({ receipt, members, amountTotal, currencyCode, disabled, onReceiptChange }: ReceiptItemsSplitProps) {
    const { data: currencies } = useCurrencies()
    const [prirazuji, setPrirazuji] = useState(false)
    const [novaPolozka, setNovaPolozka] = useState({ label: '', cena: '' })
    const [chybaNove, setChybaNove] = useState<string>()
    const [pridavam, setPridavam] = useState(false)

    // Ceny položek jsou v měně účtenky, ta se od měny útraty může lišit.
    const menaPolozek = receipt.detectedCurrencyCode ?? currencyCode
    const exponentPolozek = exponentOf(currencies, menaPolozek)
    const nepriradene = nepriradenePolozky(receipt)
    const vahy = vahyZPolozek(receipt, members)
    const stejnaMena = menaPolozek === currencyCode
    const rozdil = stejnaMena && amountTotal > 0 ? receipt.itemsTotal - amountTotal : 0

    // Kontrakt chce u úpravy vždy název a cenu, proto se doplní z položky.
    const ulozPolozku = async (item: ReceiptItem, zmeny: { label?: string; totalPrice?: number; memberIds?: string[] }) => {
        const aktualni = await actionApiCall(() =>
            updateReceiptItem({
                path: { receiptId: receipt.id, itemId: item.id },
                body: {
                    label: zmeny.label ?? item.label,
                    totalPrice: zmeny.totalPrice ?? item.totalPrice,
                    memberIds: zmeny.memberIds ?? item.memberIds,
                },
            }),
        )
        if (aktualni) {
            onReceiptChange(aktualni)
        }
    }

    const smazPolozku = async (itemId: string) => {
        const aktualni = await actionApiCall(() => deleteReceiptItem({ path: { receiptId: receipt.id, itemId } }))
        if (aktualni) {
            onReceiptChange(aktualni)
        }
    }

    const pridatPolozku = async () => {
        const totalPrice = parseAmount(novaPolozka.cena, exponentPolozek)
        if (!novaPolozka.label.trim() || totalPrice === undefined || totalPrice < 0) {
            setChybaNove('Zadejte název a cenu položky.')
            return
        }

        setPridavam(true)
        try {
            const aktualni = await actionApiCall(() =>
                addReceiptItem({ path: { receiptId: receipt.id }, body: { label: novaPolozka.label.trim(), totalPrice } }),
            )
            if (aktualni) {
                setNovaPolozka({ label: '', cena: '' })
                setChybaNove(undefined)
                onReceiptChange(aktualni)
            }
        } finally {
            setPridavam(false)
        }
    }

    // Postupně, ne naráz - každá odpověď nese celou účtenku a poslední tak
    // obsahuje všechna přiřazení.
    const priraditVsem = async () => {
        setPrirazuji(true)
        try {
            let posledni: Receipt | undefined
            for (const item of nepriradene) {
                posledni =
                    (await actionApiCall(() =>
                        updateReceiptItem({
                            path: { receiptId: receipt.id, itemId: item.id },
                            body: { label: item.label, totalPrice: item.totalPrice, memberIds: members.map((member) => member.id) },
                        }),
                    )) ?? posledni
            }
            if (posledni) {
                onReceiptChange(posledni)
            }
        } finally {
            setPrirazuji(false)
        }
    }

    return (
        <div className="mb-3" data-testid="polozky-uctenky">
            <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                <span className="small text-secondary">
                    Klepnutím na jména přiřaďte položky. Sdílenou položku si lidé rozdělí rovným dílem.
                </span>
                {nepriradene.length > 0 && (
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="flex-shrink-0"
                        disabled={disabled || prirazuji}
                        onClick={() => void priraditVsem()}
                    >
                        {prirazuji && <Spinner animation="border" size="sm" className="me-1" />}
                        Zbylé všem
                    </Button>
                )}
            </div>

            <ListGroup className="mb-2">
                {receipt.items.map((item) => (
                    <ReceiptItemRow
                        key={item.id}
                        item={item}
                        members={members}
                        currencyCode={menaPolozek}
                        exponent={exponentPolozek}
                        disabled={disabled || prirazuji}
                        onSave={(zmeny) => ulozPolozku(item, zmeny)}
                        onDelete={() => smazPolozku(item.id)}
                    />
                ))}
                {receipt.items.length === 0 && <ListGroup.Item className="small text-secondary">Zatím tu nejsou žádné položky.</ListGroup.Item>}
                <ListGroup.Item className="pb-0">
                    {/* Na úzkém displeji se cena s tlačítkem zalomí pod název. */}
                    <div className="d-flex flex-wrap gap-2 align-items-start">
                        <div className="flex-grow-1" style={{ minWidth: '12rem' }}>
                            <TextField
                                label="Chybějící položka"
                                value={novaPolozka.label}
                                onChange={(label) => setNovaPolozka((predchozi) => ({ ...predchozi, label }))}
                                placeholder="Např. Svíčková"
                                error={chybaNove}
                                disabled={disabled || pridavam}
                            />
                        </div>
                        <div className="flex-grow-1" style={{ maxWidth: '8rem' }}>
                            <AmountField
                                label="Cena"
                                value={novaPolozka.cena}
                                onChange={(cena) => setNovaPolozka((predchozi) => ({ ...predchozi, cena }))}
                                disabled={disabled || pridavam}
                            />
                        </div>
                        <Button
                            variant="outline-primary"
                            className="su-tappable mt-4 flex-shrink-0"
                            aria-label="Přidat položku"
                            disabled={disabled || pridavam}
                            onClick={() => void pridatPolozku()}
                        >
                            {pridavam ? <Spinner animation="border" size="sm" /> : <i className="bi bi-plus-lg" aria-hidden="true" />}
                        </Button>
                    </div>
                </ListGroup.Item>
            </ListGroup>

            {nepriradene.length > 0 ? (
                <Alert variant="warning" className="py-2 small mb-2">
                    Nepřiřazené položky: {nepriradene.length}. Útratu jde potvrdit, až bude každá položka někomu patřit.
                </Alert>
            ) : (
                vahy.length > 0 && (
                    <div className="small mb-2" data-testid="souhrn-polozek">
                        <div className="text-secondary mb-1">Položky podle osob</div>
                        {vahy.map(({ memberId, weight }) => (
                            <div key={memberId} className="d-flex justify-content-between">
                                <span>{members.find((member) => member.id === memberId)?.displayName}</span>
                                <span>{formatAmount(Math.round(weight), exponentPolozek)}</span>
                            </div>
                        ))}
                    </div>
                )
            )}

            {rozdil !== 0 && (
                <Alert variant="info" className="py-2 small mb-2">
                    Položky dávají dohromady {formatMoney(receipt.itemsTotal, menaPolozek, exponentPolozek)}, útrata je{' '}
                    {formatMoney(amountTotal, currencyCode, exponentOf(currencies, currencyCode))}. Rozdíl (sleva, zaokrouhlení nebo
                    chybějící položka) se rozpočítá poměrně podle toho, kdo co měl.
                </Alert>
            )}

            {!stejnaMena && (
                <Alert variant="info" className="py-2 small mb-2">
                    Ceny položek jsou v {menaPolozek}, útrata v {currencyCode}. Částka se rozdělí poměrně podle hodnoty položek.
                </Alert>
            )}
        </div>
    )
}
